import {
  QuoteActionType,
  GetQuotesStartAction,
  GetQuotesSuccessAction,
  GetQuotesFailureAction,
  ResetQuotesAction,
  SaveQuoteStartAction,
  SaveQuoteSuccessAction,
  SaveQuoteFailureAction,
  ResetQuotePriceAdjustmentsAction
} from './quote.actions';
import { createReducer } from '@common/redux-utils';
import { QuoteViewModel, QuoteSearchViewModel } from '@models/Quote';
import {
  GetQuoteDetailsStartAction,
  GetQuoteDetailsSuccessAction,
  GetQuoteDetailsFailureAction,
  SendQuoteEmailSuccessAction,
  SendQuoteEmailStartAction,
  SendQuoteEmailFailureAction,
  ConvertQuotePreventResetAction,
  ClearQuoteStateAction
} from './quote.actions';
import { PriceAdjustmentViewModel } from '@models/Product';

// Defining the state of the page.
// Should be one per page.
// All properties should be readonly.
export interface QuoteState {
  readonly message?: string;
  readonly query?: string | null;
  readonly quotesLoading?: boolean;
  readonly quotesPageCount?: number;
  readonly quotesPageNumber?: number;
  readonly quotesPageSize?: number;
  readonly quotesOrderBy?: string;
  readonly quoteSearchList?: QuoteSearchViewModel[];
  readonly quoteVM: QuoteViewModel | null;
  readonly loadingQuoteDetailsFor: number[]; // the ids of quotes currently being loaded.
  readonly quoteDetailsErrorMessage?: string;
  readonly filterByConverted: boolean;
  readonly id: number;
  readonly preventReset?: boolean;
  readonly loading?: boolean;
}

// This is the initial state of the page
const initialState: QuoteState = {
  message: '',
  query: '',
  quotesLoading: false,
  quotesPageCount: 0,
  quotesPageNumber: 1,
  quoteSearchList: [],
  quotesPageSize: 10,
  quotesOrderBy: 'id',
  quoteVM: null,
  loadingQuoteDetailsFor: [],
  filterByConverted: false,
  id: 0
};

/********** Search **********/
function handleGetQuotesStart(state: QuoteState, action: GetQuotesStartAction): QuoteState {
  return {
    ...state,
    message: 'Search Init',
    query: action.query,
    filterByConverted: action.filterByConverted,
    quotesLoading: true
  };
}

function handleGetQuotesSuccess(state: QuoteState, action: GetQuotesSuccessAction): QuoteState {
  return {
    ...state,
    message: 'Search Success!',
    quotesLoading: false,
    quotesPageCount: Math.ceil(action.result.totalResults / action.result.pageSize),
    quotesPageNumber: action.pageNumber,
    quotesPageSize: action.pageSize,
    quotesOrderBy: action.orderBy,
    quoteSearchList: action.result.results
  };
}

function handleGetQuotesFailure(state: QuoteState, action: GetQuotesFailureAction): QuoteState {
  return {
    ...state,
    message: action.errorMessage,
    quotesLoading: false
  };
}

function handleResetQuotes(state: QuoteState, action: ResetQuotesAction): QuoteState {
  return {
    ...state,
    quotesLoading: false,
    quoteSearchList: []
  };
}

/******* Save  ******/
function handleSaveQuoteStart(state: QuoteState, action: SaveQuoteStartAction): QuoteState {
  return {
    ...state,
    message: action.message
  };
}

function handleSaveQuoteSuccess(state: QuoteState, action: SaveQuoteSuccessAction): QuoteState {
  return {
    ...state,
    message: action.message,
    id: action.quoteId
  };
}

function handleSaveQuoteFailure(state: QuoteState, action: SaveQuoteFailureAction): QuoteState {
  return {
    ...state,
    message: action.errorMessage || ''
  };
}

function handleGetQuoteDetailsStart(state: QuoteState, action: GetQuoteDetailsStartAction): QuoteState {
  if (state.loadingQuoteDetailsFor.indexOf(+action.quoteId) !== -1) {
    return state;
  }

  const loadingQuotes = state.loadingQuoteDetailsFor;
  loadingQuotes.push(+action.quoteId);

  return {
    ...state,
    loadingQuoteDetailsFor: loadingQuotes
  };
}

function handleGetQuoteDetailsSuccess(state: QuoteState, action: GetQuoteDetailsSuccessAction): QuoteState {
  const loadingQuotes = state.loadingQuoteDetailsFor;

  const theIndex = loadingQuotes.indexOf(action.quoteVM.id);
  if (theIndex === -1) {
    return {
      ...state,
      quoteVM: action.quoteVM
    };
  }

  const newQuotes = loadingQuotes.filter((x, index) => {
    return index !== theIndex;
  });

  return {
    ...state,
    loadingQuoteDetailsFor: newQuotes,
    quoteVM: action.quoteVM
  };
}

function handleGetQuoteDetailsFailure(state: QuoteState, action: GetQuoteDetailsFailureAction): QuoteState {
  const loadingQuotes = state.loadingQuoteDetailsFor;

  const theIndex = loadingQuotes.indexOf(action.quoteId);
  if (theIndex === -1) {
    return {
      ...state,
      quoteDetailsErrorMessage: action.errorMessage
    };
  }

  const newQuotes = loadingQuotes.filter((x, index) => {
    return index !== theIndex;
  });

  return {
    ...state,
    loadingQuoteDetailsFor: newQuotes,
    quoteDetailsErrorMessage: action.errorMessage
  };
}

function handleSendQuoteEmailStart(state: QuoteState, action: SendQuoteEmailStartAction): QuoteState {
  return {
    ...state,
    loading: true
  };
}

function handleSendQuoteEmailSuccess(state: QuoteState, action: SendQuoteEmailSuccessAction): QuoteState {
  return {
    ...state,
    loading: false
  };
}

function handleSendQuoteEmailFailure(state: QuoteState, action: SendQuoteEmailFailureAction): QuoteState {
  return {
    ...state,
    loading: false,
    message: 'ERROR - send email failed- ' + action.errorMessage
  };
}

function handleConvertQuotePreventReset(state: QuoteState, action: ConvertQuotePreventResetAction): QuoteState {
  return {
    ...state,
    preventReset: action.preventReset
  };
}

function handleResetPriceAdjustments(state: QuoteState, action: ResetQuotePriceAdjustmentsAction): QuoteState {
  return {
    ...state,
    quoteVM: {
      ...state.quoteVM,
      priceAdjustment: {} as PriceAdjustmentViewModel,
      priceAdjustmentAmount: 0,
      priceAdjustmentNotes: ''
    } as QuoteViewModel
  };
}

function handleClearQuoteState(state: QuoteState, action: ClearQuoteStateAction): QuoteState {
  return initialState;
}

/*
function handleSendQuoteEmail(state: QuoteState, action: QuoteActionType): QuoteState {
return state;
} */

// This creates the reducer as redux wants it w/ a helper function
// There should an entry for each action type
export const QuoteReducer = createReducer(initialState, {
  /********** Search **********/
  [QuoteActionType.GetQuotesStart]: handleGetQuotesStart,
  [QuoteActionType.GetQuotesSuccess]: handleGetQuotesSuccess,
  [QuoteActionType.GetQuotesFailure]: handleGetQuotesFailure,
  [QuoteActionType.ResetQuotes]: handleResetQuotes,

  /****** Save **************/
  [QuoteActionType.SaveQuoteStart]: handleSaveQuoteStart,
  [QuoteActionType.SaveQuoteSuccess]: handleSaveQuoteSuccess,
  [QuoteActionType.SaveQuoteFailure]: handleSaveQuoteFailure,
  [QuoteActionType.ResetPriceAdjustments]: handleResetPriceAdjustments,

  /******* Get Details ******/
  [QuoteActionType.QuoteGetDetailsStart]: handleGetQuoteDetailsStart,
  [QuoteActionType.QuoteGetDetailsSuccess]: handleGetQuoteDetailsSuccess,
  [QuoteActionType.QuoteGetDetailsFailure]: handleGetQuoteDetailsFailure,

  [QuoteActionType.SendQuoteEmailStart]: handleSendQuoteEmailStart,
  [QuoteActionType.SendQuoteEmailSuccess]: handleSendQuoteEmailSuccess,
  [QuoteActionType.SendQuoteEmailFailure]: handleSendQuoteEmailFailure,

  [QuoteActionType.ConvertQuotePreventReset]: handleConvertQuotePreventReset,
  [QuoteActionType.ClearQuoteState]: handleClearQuoteState
});



// WEBPACK FOOTER //
// ./src/quote/quote.reducer.ts